import React, { memo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { transformToAgeInDays } from '../../../utils/helperFunction/transformDate';
import EmptyPage from '../../ui/EmptyPage';
import { Message, QueuedMessage } from '../../../types/Messaging/Message';
import dayjs from 'dayjs';
import Divider from '../../ui/Divider';
import { MessageCard, MessageCardSkeleton } from './MessageCard';

export type Props = {
  messages?: Array<Message | QueuedMessage>;
  userRoleId?: number;
  isFetching: boolean;
  onResend: (id: string) => void;
  observeNode?: (node: HTMLElement | null) => void;
  scrollElRef: React.RefObject<HTMLElement | null>; // ← add this
};

const isSameDay = (firstDate: string, secDate: string): boolean =>
  dayjs(firstDate).isSame(dayjs(secDate), 'day');

const isReadByUser = (readers: Message['reads'], userRoleId: number) =>
  Boolean(readers.find(({ role_id }) => role_id == userRoleId));

const ChatMessageArea = memo(function ({
  messages,
  userRoleId,
  onResend,
  isFetching,
  observeNode,
  scrollElRef,
}: Props) {
  const parentRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: messages?.length ?? 0,
    // ✅ Must point to IonContent's scroll element, not the div
    getScrollElement: () => scrollElRef.current,
    estimateSize: () => 72, // avg message height — virtualizer refines this
    overscan: 10, // render 10 items above/below viewport
    getItemKey: (i) => {
      const msg = messages![i];
      return `m:${msg.id}:${i}`;
    },
  });

  if ((!messages || !userRoleId) && !isFetching)
    return (
      <EmptyPage
        message="Conversation has no message"
        className="h-full w-full"
      />
    );

  if (isFetching)
    return (
      <div className="w-full h-full p-2 overflow-hidden">
        {[1, 2, 3, 4].map((n) => (
          <MessageCardSkeleton count={n} key={n} />
        ))}
      </div>
    );

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div ref={parentRef} className="w-full px-2">
      {/* Total height — keeps scrollbar accurate */}
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        <div
          className="space-y-6"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
          }}
        >
          {virtualItems.map((virtualRow) => {
            const msg = messages![virtualRow.index];
            const idx = virtualRow.index;
            const arr = messages!;

            const isNewDay = !isSameDay(
              msg.created_at,
              idx > 0 ? arr[idx - 1].created_at : msg.created_at
            );

            const domId =
              'reads' in msg &&
              userRoleId &&
              msg.sender_role_id !== userRoleId &&
              !isReadByUser(msg.reads, userRoleId)
                ? String(msg.id)
                : undefined;

            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                // ✅ Tells virtualizer actual rendered height for accurate positioning
                ref={virtualizer.measureElement}
              >
                {(isNewDay || idx === 0) && (
                  <div className="py-4 capitalize">
                    <Divider text={transformToAgeInDays(msg.created_at)} />
                  </div>
                )}
                <div
                  data-unread-message-id={domId}
                  ref={(el) => observeNode?.(el as HTMLElement | null)}
                >
                  <MessageCard
                    onResend={onResend}
                    {...msg}
                    userRoleId={userRoleId || 0}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export default ChatMessageArea;
